import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { api, type PuestaEnMarcha, type Rol } from "@/lib/api";
import { gruposVisibles, separarMenu } from "@/lib/navegacion";

/** La puesta en marcha del desarrollo actual, compartida con el Inicio por el contexto (un solo pedido). */
export interface EstadoPuestaEnMarcha {
  data: PuestaEnMarcha | undefined;
  error: boolean;
}

interface Pedido {
  fid: number;
  data: PuestaEnMarcha | undefined;
  error: boolean;
}

/**
 * El menú de la barra (siempre el completo del rol, Mati 2026-10-22) y la puesta en marcha para el Inicio (sin JSX).
 * Sólo para el admin pide la puesta en marcha del desarrollo actual; la vuelve a pedir en cada cambio de ruta mientras
 * no esté completa (así el Inicio se actualiza cuando el admin completa un paso) y deja de pedirla cuando se completa,
 * hasta cambiar de desarrollo.
 */
export function useMenu(rol: Rol, fid: number) {
  const { pathname } = useLocation();
  const [pedido, setPedido] = useState<Pedido>({ fid, data: undefined, error: false });
  const actual = pedido.fid === fid ? pedido : { fid, data: undefined, error: false };
  const completa = useRef(false);
  completa.current = actual.data?.completa === true;

  useEffect(() => {
    if (rol !== "admin" || completa.current) return;
    const controlador = new AbortController();
    api
      .get<PuestaEnMarcha>(`/api/fideicomisos/${fid}/puesta-en-marcha`, controlador.signal)
      .then((data) => {
        if (controlador.signal.aborted) return;
        setPedido({ fid, data, error: false });
      })
      .catch(() => {
        if (controlador.signal.aborted) return;
        // Si ya había un dato de este desarrollo, se queda (un pedido fallido no borra el checklist).
        setPedido((p) => (p.fid === fid && p.data ? p : { fid, data: undefined, error: true }));
      });
    return () => controlador.abort();
  }, [rol, fid, pathname]);

  const { barra: grupos, usuario: gruposUsuario } = separarMenu(gruposVisibles(rol));
  const puesta: EstadoPuestaEnMarcha = { data: actual.data, error: actual.error };
  return { grupos, gruposUsuario, puesta };
}
